import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

/**
 * ThemeContext - Global theme management for PCC app
 * Provides light/dark mode toggle with localStorage persistence
 */

const ThemeContext = createContext(undefined);

const STORAGE_KEY = 'pcc-theme-preference';

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load theme from localStorage on mount
  useEffect(() => {
    try {
      const storedTheme = localStorage.getItem(STORAGE_KEY);
      if (storedTheme === 'dark' || storedTheme === 'light') {
        setTheme(storedTheme);
      } else {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setTheme(prefersDark ? 'dark' : 'light');
      }
    } catch (error) {
      console.error('Error loading theme preference:', error);
    }
    setIsLoaded(true);
  }, []);

  // Apply theme class to document
  useEffect(() => {
    if (!isLoaded) return;
    
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (error) {
      console.error('Error saving theme preference:', error);
    }
  }, [theme, isLoaded]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  const isDark = theme === 'dark';

  const value = useMemo(() => ({ theme, toggleTheme, isDark }), [theme, toggleTheme, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme color constants for use in inline styles when needed
export const themeColors = {
  light: {
    background: '#F2F1EB',
    backgroundAlt: '#E3E1CF',
    text: '#272727',
    textSecondary: '#4A4A4A',
    textMuted: '#6B7280',
    card: '#FFFFFF',
    cardBorder: '#E3E1CF',
    header: '#F2F1EB',
    headerBg: '#9398DE',
    navBg: '#F2F1EB',
    navBorder: '#E3E1CF',
    navActive: '#9398DE',
    navInactive: '#808080',
    pccPrimary: '#9398DE',
    pccAccent: '#D7A072',
  },
  dark: {
    background: '#272727',
    backgroundAlt: '#333333',
    text: '#F2F1EB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    card: '#333333',
    cardBorder: '#444444',
    header: '#2D2D2D',
    headerBg: '#9398DE',
    navBg: '#2D2D2D',
    navBorder: '#444444',
    navActive: '#9398DE',
    navInactive: '#6A6A6A',
    pccPrimary: '#9398DE',
    pccAccent: '#D7A072',
  }
};

export default ThemeContext;
